/**
 * CreateHelmetTypeModal — migrated to unified modal system.
 * 3 fields: code, name, description.
 */

import { useState, useMemo, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { HardHat, HashIcon, StickyNoteIcon } from 'lucide-react';
import toast from 'react-hot-toast';
import { FormModal, FormField, useVinylSubmit, MODAL_COLORS } from '@/components/modal';
import { useCreateHelmetType } from '@/features/reports/hooks';
import type { HelmetTypeFormData } from '@/features/reports/types';

interface CreateHelmetTypeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export function CreateHelmetTypeModal({
  isOpen,
  onClose,
  onSuccess,
}: CreateHelmetTypeModalProps) {
  const { t } = useTranslation();
  const createHelmetType = useCreateHelmetType();
  const modalContentRef = useRef<HTMLDivElement>(null);
  const { isAnimating, submitWithAnimation } = useVinylSubmit({
    modalContentRef,
    vinylLabel: 'HELMET TYPE',
  });

  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      setCode('');
      setName('');
      setDescription('');
      setError('');
    }
  }, [isOpen]);

  const focusField = (id: string) => {
    requestAnimationFrame(() => {
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.focus({ preventScroll: true });
      }
    });
  };

  const validateForm = (): boolean => {
    setError('');
    if (!code.trim()) {
      setError(t('validation.codeRequired'));
      focusField('cht-code');
      return false;
    }
    if (!name.trim()) {
      setError(t('validation.nameRequired'));
      focusField('cht-name');
      return false;
    }
    return true;
  };

  const isFormValid = useMemo(() => {
    return !!(code.trim() && name.trim());
  }, [code, name]);

  const handleSubmit = () => {
    const payload: HelmetTypeFormData = {
      code: code.trim().toUpperCase(),
      name: name.trim(),
      description: description.trim() || undefined,
    };

    submitWithAnimation({
      validate: validateForm,
      mutate: () => createHelmetType.mutateAsync(payload),
      onSuccess: () => {
        toast.success(t('helmetsAdmin.createSuccess'));
        onSuccess();
        onClose();
      },
      onError: (err: any) => {
        const msg = err.response?.data?.message || '';
        if (msg.includes('already exists')) {
          setError(t('helmetsAdmin.codeExists'));
        } else {
          setError(err.response?.data?.error || msg || t('helmetsAdmin.createFailed'));
        }
      },
    });
  };

  const inputStyle = {
    backgroundColor: MODAL_COLORS.interactive,
    border: `1px solid ${MODAL_COLORS.border}`,
    color: MODAL_COLORS.text,
  };

  if (!isOpen) return null;

  return (
    <FormModal
      isOpen={isOpen}
      onClose={onClose}
      title={t('helmetsAdmin.createTitle')}
      isPending={createHelmetType.isPending}
      isAnimating={isAnimating}
      onSubmit={handleSubmit}
      onValidate={validateForm}
      isFormValid={isFormValid}
      modalContentRef={modalContentRef}
      vinylLabel="HELMET TYPE"
      error={error}
    >
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-2">
          <FormField icon={<HashIcon className="h-4 w-4" />} label={t('helmetsAdmin.code')} required>
            <input
              id="cht-code"
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              placeholder="H7"
              className="w-full p-2.5 rounded-lg font-semibold text-base focus:outline-none focus:ring-2"
              style={inputStyle}
            />
          </FormField>
          <FormField icon={<HardHat className="h-4 w-4" />} label={t('helmetsAdmin.name')} required>
            <input
              id="cht-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="H7 Helmet"
              className="w-full p-2.5 rounded-lg font-semibold text-base focus:outline-none focus:ring-2"
              style={inputStyle}
            />
          </FormField>
        </div>
        <FormField icon={<StickyNoteIcon className="h-4 w-4" />} label={t('helmetsAdmin.description')}>
          <textarea
            id="cht-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="w-full p-2.5 rounded-lg font-semibold text-base focus:outline-none focus:ring-2"
            style={{ ...inputStyle, minHeight: '80px', resize: 'vertical' as const }}
          />
        </FormField>
      </div>
    </FormModal>
  );
}
